import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useAppStore } from './store/appStore';
import { loadCardVisibilityOnLogin } from './store/cardVisibilityStore';
import authService from './shared/services/authService';

// Run language migration to fix any corrupted language settings
import './utils/languageMigration';

import DocumentClassifier from './pages/DocumentClassifier';
import PDFSecurityScanner from './pages/PDFSecurityScanner';

// Pages
import LandingPage from './pages/LandingPage';
import DocumentProcessing from './pages/DocumentProcessing';
import DocumentSetManager from './pages/DocumentSetManager';
import TransactionHistory from './pages/TransactionHistory';
import FilesPage from './pages/FilesPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import AdminLogin from './pages/AdminLogin';
import AdminShell from './layouts/AdminShell';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import ModulesPage from './pages/admin/ModulesPage';
import BillingPage from './pages/admin/BillingPage';
import ConfigurationPage from './pages/admin/ConfigurationPage';
import SuperAdminTenants from './pages/SuperAdminTenants';
import SuperAdminUsers from './components/SuperAdminUsers';
import AuditLogsViewer from './components/AuditLogsViewer';
import FieldRegistryTable from './components/admin/FieldRegistryTable';
import AdminApiClientsTab from './components/AdminApiClientsTab';
import ATLASStructureViewer from './components/ATLASStructureViewer';
import AtlasDebugPanel from './components/AtlasDebugPanel';
import FeatureCardVisibilityPanel from './components/admin/FeatureCardVisibilityPanel';
import CodelistRefreshPanel from './components/admin/CodelistRefreshPanel';
import ComplianceDashboard from './pages/ComplianceDashboard';
import CustomsDeclarationPage from './pages/CustomsDeclarationPage';
import EmailProcessor from './pages/EmailProcessor';
import TamperingDetection from './pages/TamperingDetection';
import NLPRules from './pages/NLPRules';
import CustomsAuditPage from './pages/CustomsAuditPage';
import ExportStatusPage from './pages/ExportStatusPage';
import NotFound from './pages/NotFound';
import { TariffClassifierPage } from './features/tariff_classifier';
import { ExportControlPage } from './features/export_control';
import SupplierDeclarationPage from './features/supplier_declarations/pages/SupplierDeclarationPage';
import MasterDataPage from './features/master_data/pages/MasterDataPage';

// Layout
import AppShell from './layouts/AppShell';

// Components
import LoadingSpinner from './shared/components/LoadingSpinner';
import ProtectedRoute from './shared/components/ProtectedRoute';
import AdminRoute from './shared/components/AdminRoute';
import KeyboardShortcutsDialog from './components/KeyboardShortcutsDialog';
import QuickSearch from './components/QuickSearch';
import { ChatWidget } from './components/ChatWidget';

// Hooks
// import useKeyboardShortcuts from './hooks/useKeyboardShortcuts'; // DISABLED - Freezing shortcuts as requested

function App() {
  const { isAuthenticated, isLoading, token, user, setAuth, clearAuth, setLoading } = useAuthStore();
  const { setShowLoginModal } = useAppStore();

  // Initialize keyboard shortcuts - DISABLED
  // const { showHelp, setShowHelp, showSearch, setShowSearch } = useKeyboardShortcuts();
  const [showHelp, setShowHelp] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      if (token) {
        try {
          setLoading(true);
          const userData = await authService.getCurrentUser();
          setAuth(userData, token);
        } catch (error) {
          console.error('Failed to verify authentication:', error);
          clearAuth();
        } finally {
          setLoading(false);
        }
      }
    };

    initializeAuth();
  }, []);

  // Load card visibility when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      loadCardVisibilityOnLogin();
    }
  }, [isAuthenticated]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="App">
      {/* Global Keyboard Shortcuts Dialogs */}
      <KeyboardShortcutsDialog open={showHelp} onClose={() => setShowHelp(false)} />
      <QuickSearch open={showSearch} onClose={() => setShowSearch(false)} />

      {/* AI Chat Assistant - Available for authenticated users */}
      {isAuthenticated && <ChatWidget />}

      <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={
            isAuthenticated
              ? <Navigate to={user?.is_admin ? "/admin/dashboard" : (user?.pinned_nav_path || "/process")} replace />
              : <LandingPage />
          }
        />

        {/* Shell-wrapped Protected Routes — AppShell provides AppBar + left nav */}
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route path="/process" element={<DocumentProcessing />} />
          <Route path="/document-sets" element={<DocumentSetManager />} />
          <Route path="/transactions" element={<TransactionHistory />} />
          <Route path="/files" element={<FilesPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/compliance" element={<ComplianceDashboard />} />
          <Route path="/customs-declaration" element={<CustomsDeclarationPage />} />
          <Route path="/customs-declaration/:id" element={<CustomsDeclarationPage />} />
          <Route path="/tariff-classifier" element={<TariffClassifierPage />} />
          <Route path="/email-processor" element={<EmailProcessor />} />
          <Route path="/tampering-detection" element={<TamperingDetection />} />
          <Route path="/customs-audit" element={<CustomsAuditPage />} />
          <Route path="/process/export-status" element={<ExportStatusPage />} />
          <Route path="/export-control" element={<ExportControlPage />} />
          <Route path="/document-classifier" element={<DocumentClassifier />} />
          <Route path="/pdf-security-scanner" element={<PDFSecurityScanner />} />
          <Route path="/nlp-rules" element={<NLPRules />} />
          <Route path="/supplier-declarations" element={<SupplierDeclarationPage />} />
          <Route path="/master-data" element={<MasterDataPage />} />
        </Route>

        {/* Admin Routes — sidebar-based AdminShell layout */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminShell />
            </AdminRoute>
          }
        >
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboardPage />} />
          <Route path="tenants" element={<SuperAdminTenants embedded />} />
          <Route path="super-admins" element={<SuperAdminUsers />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="modules" element={<ModulesPage />} />
          <Route path="audit-logs" element={<AuditLogsViewer />} />
          <Route path="field-registry" element={<FieldRegistryTable />} />
          <Route path="feature-cards" element={<FeatureCardVisibilityPanel />} />
          <Route path="data-settings" element={<CodelistRefreshPanel />} />
          <Route path="api-clients" element={<AdminApiClientsTab />} />
          <Route path="atlas-structure" element={<ATLASStructureViewer />} />
          <Route path="atlas-debug" element={<AtlasDebugPanel />} />
          <Route path="configuration" element={<ConfigurationPage />} />
        </Route>

        {/* Legacy tenant-admin paths — tenant management now lives in the
            Settings page (Users / Organisation tabs, tenant-admin only). */}
        <Route path="/tenant/settings" element={<Navigate to="/settings" replace />} />
        <Route path="/tenant/users" element={<Navigate to="/settings" replace />} />

        {/* 404 Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
}

export default App;